/* eslint-disable */
import axios from 'axios';
import { SubscriptionSession } from '@root/lib/classes/SubscriptionSession';
import { EmptyGiftCardError, GiftCardNotFoundError } from '@root/lib/errors/SentinelErrors';
import CustomerNotLoggedInError from '@root/lib/errors/CustomerNotLoggedInError';
import RequiredError from '@root/lib/errors/RequiredError';
import dayjs from '@root/lib/packages/dayjs';
import MenuWeek from '@root/lib/classes/MenuWeek';
import { BOX_ID } from '@root/lib/constants';
import * as MiddlewareService from './MiddlewareService';
import * as LogicService from './LogicService';

/**
 * @typedef {Object} PageConfig
 * @property {number} [page]
 * @property {number} [perPage]
 */

const clients = {
  subu: axios.create({
    baseURL: 'https://api.boldcommerce.com/subscription_builder'
  }),
  bsubs: axios.create({
    baseURL: 'https://sub.boldapps.net/api',
    withCredentials: true,
  }),
};
clients.bsubs.interceptors.request.use((config) => {
  config.params = {
    ...config.params,
    shop_url: Shopify.shop,
  }
  return config;
});


/**
 * Gets information about the subscription group the product is in.
 *
 * @param {number} productId
 *
 * @returns {null|{
 *   id: number;
 *   billing_rules: Array<{
 *     id: number;
 *     billing_rule: string;
 *     interval_name: string;
 *     subscription_group_id: number;
 *     custom_billing_rule: string;
 *   }>;
 * }}
 */
const getSubscriptionInformationForProductId = (productId) => {
  return BOLD.subscriptions.getSubscriptionGroupFromProductId(productId);
}

/**
 * Gets the currently logged in customer's platform data.
 *
 * @returns {Promise<{
 *   jwt: string;
 *   customerId: string;
 * }>}
 */
const getPlatformData = () => {
  return new Promise((resolve, reject) => {
    BOLD.subscriptions.getJWT(data => {
      if (data.success) return resolve(data.value);
      switch (data.error) {
        case 'not_logged_in':
          return reject(new CustomerNotLoggedInError());
        default:
          return reject(new Error(data.error));
      }
    });
  });
}

const exchangePlatformJwt = async (customerId, platformJwt) => {
  const { data } = await clients.bsubs.get('/customer/login', {
    params: {
      platform_customer_id: customerId,
      customer_jwt: platformJwt,
    },
  });

  return {
    jwt: data.subscriptionsWebToken,
    expiry: data.subscriptionsWebTokenExpTime,
    customer: {
      id: data.customer.bold_platform_id,
      cashierPublicId: data.customer.cashier_public_id,
      platformId: data.customer.platform_id,
    }
  };
}

/**
 * Gets complete subscription information.
 *
 * @param {SubscriptionSession} subscriptionSession
 * @param {() => boolean} isCancelled
 */
const getCompleteSubscriptionInformation = async (subscriptionSession, isCancelled = () => false) => {
  if (isCancelled()) return;
  const sess = subscriptionSession;

  const menuWeekPromise = MenuWeek.serverNow();

  // Getting the customer's active subscriptions. If customer has no subscriptions, proceed to initial flow.
  const subscription = await subscriptions.find(sess, {}, (subscription) => {
    return subscription.subscription_status !== 'inactive';
  });
  if (!subscription || isCancelled()) return;

  // Information below isn't for non my choice subscriptions so if the subscription is not for my choice
  // we just return the subscription
  if (!subscription.line_items[0].sku.startsWith('my-choice')) {
    return {
      subscription,
    };
  }
  
  const menuWeek = (await menuWeekPromise).next(2);
  if (isCancelled()) return;
  
  const subscriptionId = subscription.bold_platform_subscription_id;
  const subscriptionLineItemId = subscription.line_items[0].bold_platform_subscription_line_item_id;
  const zip = subscription.shipping_address.zip;
  const deliveryDatePromise = MiddlewareService.getDeliveryDay(subscriptionId).then(menuWeek.calculateDeliveryDate);
  const futureOrderPromise = futureOrders.forMenuWeek(sess, subscriptionId, menuWeek);

  const region = await MiddlewareService.getRegion(zip);
  if (isCancelled()) return;

  const futureOrder = await futureOrderPromise;
  if (isCancelled()) return;

  const selection = await selections.find(sess, {order_datetime: futureOrder?.date}, (selection) => {
    return selection.bold_platform_subscription_line_item_id === subscriptionLineItemId;
  });
  if (isCancelled()) return;

  // Getting the time slot the selection refers to or using the menu week
  const isLocal = region.zone === 'local';
  const timeSlot = !selection 
    ? await timeSlots.forMenuWeek(BOX_ID, menuWeek, isLocal)
    : await timeSlots.find(BOX_ID, {}, slot => slot.id === selection.time_slot_id);
  if (isCancelled()) return;

  const deliveryDate = await deliveryDatePromise;
  if (isCancelled()) return;

  return {
    subscription,
    timeSlot,
    futureOrder,
    region,
    selection,
    deliveryDate,
  };
}
/**
 *
 * @param {SubscriptionSession} subscriptionSession
 * @param {{
 *   bold_platform_subscription_line_item_id: number;
 *   order_datetime: string;
 *   time_slot_id: number;
 *   line_items: Array<{
 *     box_product_id: number;
 *     choice_quantity: number;
 *   }>;
 * }} selection
 */
const createSelection = async (subscriptionSession, selection) => {
  const platformCustomerId = await subscriptionSession.getPlatformCustomerId();
  const platformJwt = await subscriptionSession.getPlatformJwt();
  const shopId = BOLD.subscriptions.config.shopPlatformId;

  const url = `/customer/v1/shops/${shopId}/customers/${platformCustomerId}/selections`;
  const { data } = await clients.subu.post(url, {selection}, {
    params: {
      token: platformJwt,
    },
  });

  return data.data.selection;
}

const futureOrders = {
  /**
   * Lists upcoming subscription orders
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   * @param {{
   *   page?: number;
   *   perPage?: number;
   * }} [params]
   * @returns
   */
  list: async (subscriptionSession, subscriptionId, {page = 1, perPage = 50} = {}) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    const { data } = await clients.bsubs.get(`/customer/subscriptions/${subscriptionId}/future_orders`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
      params: {
        per_page: perPage,
        page,
      },
    });

    return data;
  },

  /**
   * Gets a future order for a subscription that is in the passed manu week. If none found, undefined
   * is returned
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   * @param {MenuWeek} menuWeek
   */
  forMenuWeek: async (subscriptionSession, subscriptionId, menuWeek) => {
    const { future_orders: orders } = await futureOrders.list(subscriptionSession, subscriptionId);
    const futureOrder = orders.find(futureOrder => {
      // Delivery week is the date/week the order is to arrive. The date of the future order is when
      // the order is to be created in Shopify. Orders are created the week before they are meant for delivery.
      const deliverWeek = dayjs.utc(futureOrder.date, 'YYYY-MM-DDTHH:mm:ss[Z]').add(7, 'days');
      return menuWeek.contains(deliverWeek);
    });

    return futureOrder;
  },
};

const timeSlots = {
  /**
   * Gets all pages for time slots that match the provided params and passes the results
   * to the predicate until it returns true or there are no more results. The first result
   * to return true from the predicate will be returned
   * 
   * @param {number} boxId
   * @param {{
   *   order_datetime: string;
   * }} params
   * @param {(timeSlot: Object) => boolean} predicate
   */
  find: async (boxId, params, predicate) => {
    const shopId = BOLD.subscriptions.config.shopPlatformId;
    let page = 1;
    let timeSlot;

    // Looping until the desired time slot is found
    do {
      // Getting all time slots that can be purchased on the date provided
      const resp = await clients.subu.get(`/storefront/v1/shops/${shopId}/boxes/${boxId}/time_slots`, {
        params: {
          ...params,
          page,
        },
      });

      // Finding correct slot
      const slots = resp.data.data.time_slots;
      timeSlot = slots.find(predicate);

      // Stopping if this is the last page
      const pagination = resp.data.pagination;
      if (page >= pagination.total_pages) break;
      page++;
    } while (!timeSlot)

    return timeSlot;
  },

  /**
   * Gets a time slot for the provided order date
   *
   * @param {number} boxId
   * @param {Date|dayjs.Dayjs} orderDate
   * @param {boolean} local
   */
  forOrderDate: async (boxId, orderDate, local = false) => {
    const menuId = LogicService.calculateMenuId(orderDate);

    // Need to go back a week since the time slot start and end dates are setup so that they
    // denote when the time slot can be accessed, not when the order for the time slot is to be delivered.
    // Don't ask me why, idk either
    orderDate = dayjs(orderDate).subtract(7, 'days');

    const slotName = menuId + (local ? '_loc' : '_nat');
    const dtStr = orderDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    const timeSlot = await timeSlots.find(boxId, {order_datetime: dtStr}, slot => slot.time_slot_name === slotName);

    if (!timeSlot) {
      throw new Error('Menu for order date could not be retrieved');
    }

    return timeSlot;
  },

  /**
   * Gets a time slot for the passed menu week if one exists.
   *
   * @param {number} boxId
   * @param {MenuWeek} menuWeek
   * @param {boolean} local
   */
  forMenuWeek: async (boxId, menuWeek, local = false) => {
    return await timeSlots.forOrderDate(boxId, menuWeek.startDate, local);
  },
};

/**
 * @typedef {Object} Selection
 * @property {number} id
 * @property {number} bold_platform_subscription_line_item_id
 * @property {string} order_datetime
 * @property {number} box_size_id
 * @property {number} time_slot_id
 * @property {Array<{
 *   box_product_id: number;
 *   choice_quantity: number;
 * }>} line_items
 * @property {string} created_at
 * @property {string} updated_at
 */
const selections = {
  /**
   * Gets a page of selections
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {{
   *   page?: number;
   *   order_datetime?: string;
   * }} params
   * @returns {{
   *   pagination: {
   *     total_pages: number;
   *   };
   *   selections: Array<Selection>;
   * }}
   */
  list: async (subscriptionSession, params) => {
    const shopId = BOLD.subscriptions.config.shopPlatformId;
    const platformCustomerId = await subscriptionSession.getPlatformCustomerId();

    const { data } = await clients.subu.get(`/customer/v1/shops/${shopId}/customers/${platformCustomerId}/selections`, {
      params: {
        ...params,
        token: await subscriptionSession.getPlatformJwt(),
      },
    });

    return {
      selections: data.data.selections,
      pagination: data.pagination,
    }
  },

  /**
   * Returns the first selection where the predicate is true
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {{
   *   order_datetime: string;
   * }} params
   * @param {(selection: Selection) => boolean} predicate
   * @returns {Selection|undefined}
   */
  find: async (subscriptionSession, params, predicate) => {
    let selection;
    let page = 1;

    // Looping until we find the correct selection or we run out of results
    do {
      const { selections: _selections, pagination } = await selections.list(subscriptionSession, {
        ...params,
        page,
      })

      selection = _selections.find(predicate);

      // Stopping loop if we are on the last page
      if (page >= pagination.total_pages) break;
      page++;
    } while(!selection);

    return selection;
  }
}

const subscriptions = {
  /**
   * Gets a subscription by its ID
   * 
   * @param {SubscriptionSession} subscriptionSession 
   * @param {number} subscriptionId 
   */
  get: async (subscriptionSession, subscriptionId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    // Getting the customer's subscriptions. Should only at most have one
    const { data } = await clients.bsubs.get(`/customer/subscriptions/${subscriptionId}`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.subscription;
  },

  /**
   * Lists subscriptions for the customer. Includes inactive subscriptions
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {PageConfig} [params]
   * @returns {{
   *   pagination: Object;
   *   subscriptions: Array<{
   *     bold_platform_subscription_id: number;
   *     bold_platform_subscription_line_item_id: number;
   *     shipping_address: {
   *        zip: string;
   *     }
   *   }>;
   * }}
   */
  list: async (subscriptionSession, params) => {
    const boldCustomerId = await subscriptionSession.getBoldCustomerId();
    const boldJwt = await subscriptionSession.getBoldJwt();

    // Getting the customer's subscriptions. Should only at most have one
    const { data } = await clients.bsubs.get(`/customer/customers/${boldCustomerId}/subscriptions`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
      params: {
        per_page: params?.perPage,
        page: params?.page,
      },
    });

    return data;
  },

  /**
   * Returns the first subscription where the predicate is true
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {{}} [params] Placeholder for future use
   * @param {*} predicate
   */
  find: async (subscriptionSession, params, predicate) => {
    let subscription;
    let page = 1;

    // Looping until we find the subscription we're looking for or run out of pages
    do {
      const { subscriptions: subs, pagination } = await subscriptions.list(subscriptionSession, {
        ...params,
        page,
      });

      subscription = subs.find(predicate);
      if (page >= pagination.total_pages) break;
      page++;
    } while(!subscription);

    return subscription;
  },

  /**
   * Skips a future order. futureOrderDate must exactly matches a future order on the subscription
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   * @param {string} futureOrderDate
   */
  skip: async (subscriptionSession, subscriptionId, futureOrderDate) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    await clients.bsubs.put(`/customer/subscriptions/${subscriptionId}/skip`, {
      date: futureOrderDate,
    }, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });
  },

  /**
   * @param {Object} subscriptionData
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   * @param {Object} subscriptionData
   */
  partiallyUpdate: async (subscriptionSession, subscriptionId, subscriptionData) => {
    const boldJwt = await subscriptionSession.getBoldJwt();
    let subscription = subscriptionData;

    const { data } = await clients.bsubs.patch(`/customer/subscriptions/${subscriptionId}`, {subscription}, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.subscription;
  },

  /**
   * Unskips a future order. futureOrderDate must exactly matchs a futrue order on the subscription
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   * @param {string} futrueOrderDate
   */
   unskip: async (subscriptionSession, subscriptionId, futrueOrderDate) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    await clients.bsubs.put(`/customer/subscriptions/${subscriptionId}/recover`, {
      date: futrueOrderDate,
    }, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });
  },

  /**
   * Retrieve a list of swappable products for a subscription line item.

   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   * @param {number} lineItemId
   */
  listSwappableProducts: async (subscriptionSession, subscriptionId, lineItemId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    const { data } = await clients.bsubs.get(`/customer/subscriptions/${subscriptionId}/line_items/${lineItemId}/products_swap`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.swappable_products;
  },

  /**
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   * @param {number} lineItemId
   * @param {{
   *   variantId: number;
   *   productId: number;
   * }} variant
   * @param {number} subscriptionGroupId
   */
  swapProduct: async (subscriptionSession, subscriptionId, lineItemId, variant, subscriptionGroupId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    const { data } = await clients.bsubs.put(`/customer/subscriptions/${subscriptionId}/products_swap`, {
      swap_products: [{
        bold_platform_line_item_id: lineItemId,
        platform_product_id: variant.productId.toString(),
        platform_variant_id: variant.variantId.toString(),
        subscription_group_id: subscriptionGroupId,
      }],
    }, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.subscription;
  },

  /**
   * Gets payment information for the subscription
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   */
  getPaymentMethod: async (subscriptionSession, subscriptionId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    const { data } = await clients.bsubs.get(`/customer/subscriptions/${subscriptionId}/payment_method`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.payment_method;
  },

  /**
   * Lists all payment methods for the subscription
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   */
  listPaymentMethods: async (subscriptionSession, subscriptionId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    const { data } = await clients.bsubs.get(`/customer/subscriptions/${subscriptionId}/payment_methods`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.payment_methods;
  },

  /**
   * Lists alternative payments (gift cards) applied to the subscription
   * 
   * @param {SubscriptionSession} subscriptionSession 
   * @param {number} subscriptionId
   * 
   * @returns {Array<{
   *  payment_method_token: string,
   *  card_number: string,
   *  balance: number,
   * }>}
   */
  listAlternativePayments: async (subscriptionSession, subscriptionId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();
    
    const { data } = await clients.bsubs.get(`/customer/subscriptions/${subscriptionId}/alternative_payments`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      }
    });

    return data.gift_cards;
  },
  
  /**
   * Removes an alternative payment from a subscription
   * 
   * @param {SubscriptionSession} subscriptionSession 
   * @param {number} subscriptionId 
   * @param {string} paymentId 
   */
  deleteAlternativePayment: async (subscriptionSession, subscriptionId, paymentId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();
    
    await clients.bsubs.delete(`/customer/subscriptions/${subscriptionId}/alternative_payments/${paymentId}`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      }
    });
  },

  /**
   * Creates an alternative payment on a subscription
   * 
   * @param {SubscriptionSession} subscriptionSession 
   * @param {number} subscriptionId 
   * @param {'gift_card'} type 
   * @param {{
   *  card_number: string,
   *  security_key: string,
   * }} cardData
   * 
   * @returns {{
   *  id?: number,
   *  subscription_id: number,
   *  payment_method_token: string,
   * }}
   */
  createAlternativePayment: async (subscriptionSession, subscriptionId, type, cardData) => {
    // Checking data before sending
    if (!cardData.card_number) throw new RequiredError('Gift card number is required');
    if (!cardData.security_key) throw new RequiredError('CVC is required');

    const boldJwt = await subscriptionSession.getBoldJwt();
    const body = {alternative_payment_methods: [{type, payment_details: cardData}]};
    
    try {
      const { data } = await clients.bsubs.post(`/customer/subscriptions/${subscriptionId}/alternative_payments`, body, {
        headers: {
          Authorization: `Bearer ${boldJwt}`,
        }
      });

      return data.alternative_payment_method[0];
    } catch (e) {
      if (!e?.response.data?.errors) throw e;
      const errors = e.response.data.errors;

      if (errors.message?.includes('Gift card not found')) {
        throw GiftCardNotFoundError;
      } else if (errors.message?.includes('Gift card is empty')) {
        throw EmptyGiftCardError;
      }else if (errors['alternative_payment_methods.0.payment_details.card_number']?.[0] === 'Gift card number is required') {
        throw new RequiredError('Gift card number is required');
      } else if (errors['alternative_payment_methods.0.payment_details.security_key']?.[0] === 'Security key is required') {
        throw new RequiredError('CVC is required');
      }

      throw e;
    }
  },

  /**
   * Reactivates a cancelled subscription
   * 
   * @param {SubscriptionSession} subscriptionSession 
   * @param {number} subscriptionId 
   */
  reactivate: async (subscriptionSession, subscriptionId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    const { data } = await clients.bsubs.post(`/customer/subscriptions/${subscriptionId}/activate`, undefined, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.subscription;
  },

  /**
   * Lists the subscriptions previous orders that have been created in Shopify
   * 
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} subscriptionId
   * @param {Object} params
   */
  listOrders: async (subscriptionSession, subscriptionId, params) => {
    const boldJwt = await subscriptionSession.getBoldJwt();

    const { data } = await clients.bsubs.get(`/customer/subscriptions/${subscriptionId}/orders`, {
      params,
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.subscription_orders;
  },
}

const addresses = {
  /**
   * Creates an address in bsubs
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {Object} address
   */
  create: async (subscriptionSession, address) => {
    const boldJwt = await subscriptionSession.getBoldJwt();
    const customerId = await subscriptionSession.getBoldCustomerId();

    const { data } = await clients.bsubs.post(`/customer/customers/${customerId}/addresses`, {customer_address: address}, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.customer_address;
  },

  /**
   * Lists all the customer's addresses
   *
   * @param {SubscriptionSession} subscriptionSession
   */
  list: async (subscriptionSession) => {
    const boldJwt = await subscriptionSession.getBoldJwt();
    const customerId = await subscriptionSession.getBoldCustomerId();

    const { data } = await clients.bsubs.get(`/customer/customers/${customerId}/addresses`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.customer_addresses;
  },

  /**
   * Updates an existing address
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {Object} address
   */
  update: async (subscriptionSession, address) => {
    const boldJwt = await subscriptionSession.getBoldJwt();
    const customerId = await subscriptionSession.getBoldCustomerId();
    const addressId = address.bold_platform_id;

    const { data } = await clients.bsubs.put(`/customer/customers/${customerId}/addresses/${addressId}`, {customer_address: address}, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });

    return data.customer_address;
  },

  /**
   * Deletes and existing address
   *
   * @param {SubscriptionSession} subscriptionSession
   * @param {number} addressId
   */
  delete: async (subscriptionSession, addressId) => {
    const boldJwt = await subscriptionSession.getBoldJwt();
    const customerId = await subscriptionSession.getBoldCustomerId();

    await clients.bsubs.delete(`/customer/customers/${customerId}/addresses/${addressId}`, {
      headers: {
        Authorization: `Bearer ${boldJwt}`,
      },
    });
  },
}

export {
  addresses,
  timeSlots,
  selections,
  futureOrders,
  subscriptions,
  createSelection,
  getPlatformData,
  exchangePlatformJwt,
  getCompleteSubscriptionInformation,
  getSubscriptionInformationForProductId,
}
